import { useState, useMemo, useCallback } from 'react';
import { USERS, PROSPECTS, PRIORITY_ORDER, STAGE_ORDER, TYPE_LABELS, STAGES } from './data';
import AgentPane from './AgentPane';
import './App.css';

// ── HELPERS ───────────────────────────────────────────────────────────────────
const priorityClass = { Critical:'b-critical', High:'b-high', Medium:'b-medium', Low:'b-low' };
const stageClass = { Active:'s-active', Negotiation:'s-negotiation', 'Discovery / Technical Validation':'s-disc-tech',
  'Pilot / Partnership Business Case':'s-pilot', 'Pilot / Business Case / Negotiation':'s-pilot',
  Discovery:'s-discovery', Outreach:'s-outreach', Pipeline:'s-pipeline', Won:'s-won' };
const typeClass = { partner:'type-partner', customer:'type-customer', federal:'type-federal', investor:'type-investor' };

function Badge({ cls, label }) {
  return <span className={`badge ${cls}`}>{label}</span>;
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const handle = (e) => {
    e.preventDefault();
    const u = USERS.find(u => u.email.toLowerCase() === email.trim().toLowerCase() && u.password === pw);
    if (u) onLogin(u); else setErr('Invalid credentials.');
  };
  return (
    <div className="login-screen">
      <div className="login-logo">⬡ OrchestrateX</div>
      <div className="login-sub">Quantum Intelligence · CRM</div>
      <form className="login-card" onSubmit={handle}>
        <h2>Secure sign-in</h2>
        <div className="field"><label>Email</label>
          <input type="text" value={email} autoComplete="off" placeholder="you@orcx.ai"
            onChange={e => { setEmail(e.target.value); setErr(''); }} /></div>
        <div className="field"><label>Password</label>
          <input type="password" value={pw} autoComplete="off" placeholder="••••••••"
            onChange={e => { setPw(e.target.value); setErr(''); }} /></div>
        <button type="submit" className="login-btn">Sign in →</button>
        {err && <p className="login-err">{err}</p>}
      </form>
    </div>
  );
}

// ── DETAIL PANEL ──────────────────────────────────────────────────────────────
function DetailPanel({ record, onClose }) {
  if (!record) return null;
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="detail-panel open">
        <div className="detail-header">
          <div>
            <div className="detail-name">{record.contact !== '—' && record.contact ? record.contact : record.company}</div>
            <div className="detail-co">{record.company}{record.title ? ` · ${record.title}` : ''}</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="detail-body">
          <section className="detail-section">
            <div className="detail-section-title">Classification</div>
            <div className="detail-row"><span className="detail-key">Type</span><Badge cls={typeClass[record.type]} label={TYPE_LABELS[record.type] || record.type} /></div>
            <div className="detail-row"><span className="detail-key">Vertical</span><span className="detail-val">{record.vertical || '—'}</span></div>
            <div className="detail-row"><span className="detail-key">Priority</span><Badge cls={priorityClass[record.priority]} label={record.priority} /></div>
            <div className="detail-row"><span className="detail-key">Stage</span><Badge cls={`stage-badge ${stageClass[record.stage] || ''}`} label={record.stage} /></div>
            {record.owner && <div className="detail-row"><span className="detail-key">Owner</span><span className="detail-val">{record.owner}</span></div>}
            {record.lastContact && <div className="detail-row"><span className="detail-key">Last contact</span><span className="detail-val">{record.lastContact}</span></div>}
            {record.targetClose && <div className="detail-row"><span className="detail-key">Target close</span><span className="detail-val">{record.targetClose}</span></div>}
          </section>
          <section className="detail-section">
            <div className="detail-section-title">Commercial</div>
            <div className="detail-row"><span className="detail-key">Revenue Size</span><span className="detail-val">{record.revenueSize || '—'}</span></div>
            <div className="detail-row"><span className="detail-key">ARR Range</span><span className="detail-val arr-val">{record.arr}</span></div>
            <div className="detail-row"><span className="detail-key">Gross Margin</span><span className="detail-val">{record.margin}</span></div>
            <div className="detail-row"><span className="detail-key">Integration</span><span className="detail-val muted-sm">{record.integration}</span></div>
          </section>
          {record.oxDrivers && (
            <section className="detail-section">
              <div className="detail-section-title">OX Strategic Drivers</div>
              <div className="notes-text">{record.oxDrivers}</div>
            </section>
          )}
          {record.buyerDrivers && (
            <section className="detail-section">
              <div className="detail-section-title">Buyer Drivers</div>
              <div className="notes-text">{record.buyerDrivers}</div>
            </section>
          )}
          {record.productAsks && (
            <section className="detail-section">
              <div className="detail-section-title">Product Asks</div>
              <div className="notes-text">{record.productAsks}</div>
            </section>
          )}
          {record.tags && record.tags.length > 0 && (
            <section className="detail-section">
              <div className="detail-section-title">Tags</div>
              <div className="tags-wrap">{record.tags.map(t => <span key={t} className="tag">{t}</span>)}</div>
            </section>
          )}
          {record.artifacts && (
            <section className="detail-section">
              <div className="detail-section-title">Key Artifacts</div>
              <div className="notes-text">{record.artifacts}</div>
            </section>
          )}
          <section className="detail-section">
            <div className="detail-section-title">Intelligence Notes</div>
            <div className="notes-text">{record.notes}</div>
          </section>
        </div>
        <div className="action-row">
          <button className="action-btn" onClick={onClose}>✉ Draft outreach</button>
          <button className="action-btn primary" onClick={onClose}>Update stage →</button>
        </div>
      </aside>
    </>
  );
}

// ── STATS ROW ─────────────────────────────────────────────────────────────────
function StatsRow({ data }) {
  const critCount = PROSPECTS.filter(x => x.priority === 'Critical').length;
  const activeCount = PROSPECTS.filter(x => ['Active','Negotiation','Pilot / Partnership Business Case','Pilot / Business Case / Negotiation'].includes(x.stage)).length;
  const partnerCount = PROSPECTS.filter(x => x.type === 'partner').length;
  return (
    <div className="stats-row">
      <div className="stat-card"><div className="stat-label">Total Prospects</div><div className="stat-val">{PROSPECTS.length}</div><div className="stat-sub">All types · {data.length} shown</div></div>
      <div className="stat-card"><div className="stat-label">Critical Priority</div><div className="stat-val">{critCount}</div><div className="stat-sub">Immediate action</div></div>
      <div className="stat-card"><div className="stat-label">Active / Pilot / Negotiation</div><div className="stat-val">{activeCount}</div><div className="stat-sub">In motion</div></div>
      <div className="stat-card"><div className="stat-label">Channel Partners</div><div className="stat-val">{partnerCount}</div><div className="stat-sub">GTM partners</div></div>
    </div>
  );
}

// ── TABLE ─────────────────────────────────────────────────────────────────────
const COL_WIDTHS = ['24%','10%','9%','14%','13%','22%','8%'];
const HEADERS = [
  { k:'company', l:'Company / Contact' }, { k:'type', l:'Type' }, { k:'priority', l:'Priority' },
  { k:'stage', l:'Stage' }, { k:'arr', l:'ARR Range' }, { k:'integration', l:'Integration' }, { k:'margin', l:'Margin' },
];

function ProspectsTable({ data, onSelect, highlightId }) {
  const [sortKey, setSortKey] = useState('priority');
  const [sortDir, setSortDir] = useState(1);

  const sorted = useMemo(() => [...data].sort((a, b) => {
    if (sortKey === 'priority') return sortDir * (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);
    if (sortKey === 'stage') return sortDir * ((STAGE_ORDER[a.stage] || 9) - (STAGE_ORDER[b.stage] || 9));
    if (sortKey === 'arr') {
      const va = parseFloat(a.arr?.replace(/[^0-9.]/g,'')) || 0;
      const vb = parseFloat(b.arr?.replace(/[^0-9.]/g,'')) || 0;
      return sortDir * (va - vb);
    }
    return sortDir * (a[sortKey] || '').localeCompare(b[sortKey] || '');
  }), [data, sortKey, sortDir]);

  const doSort = k => { if (k === sortKey) setSortDir(d => d * -1); else { setSortKey(k); setSortDir(1); } };

  return (
    <div className="table-wrap">
      <table>
        <colgroup>{COL_WIDTHS.map((w,i) => <col key={i} style={{width:w}} />)}</colgroup>
        <thead><tr>{HEADERS.map(h => (
          <th key={h.k} onClick={() => doSort(h.k)}>
            {h.l}<span className="sort-icon">{sortKey===h.k?(sortDir>0?' ↑':' ↓'):' ↕'}</span>
          </th>
        ))}</tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={7} className="empty">No prospects match your filters.</td></tr>}
          {sorted.map(r => (
            <tr key={r.id} onClick={() => onSelect(r)} className={highlightId === r.id ? 'row-highlight' : ''}>
              <td>
                <div className="td-company">{r.company}</div>
                {r.contact && r.contact !== '—' && <div className="td-contact">{r.contact}{r.title ? ` · ${r.title}` : ''}</div>}
              </td>
              <td><Badge cls={typeClass[r.type]} label={TYPE_LABELS[r.type] || r.type} /></td>
              <td><Badge cls={priorityClass[r.priority]} label={r.priority} /></td>
              <td><span className={`stage-badge ${stageClass[r.stage] || ''}`}>{r.stage}</span></td>
              <td className="td-arr">{r.arr}</td>
              <td className="td-muted td-sm">{r.integration}</td>
              <td className="td-muted td-sm">{r.margin}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── PIPELINE ──────────────────────────────────────────────────────────────────
function Pipeline({ data, onSelect }) {
  return (
    <div className="pipeline-cols">
      {STAGES.map(stage => {
        const items = data.filter(r => r.stage === stage);
        return (
          <div key={stage} className="pipe-col">
            <div className="pipe-col-header"><span>{stage}</span><span className="pipe-count">{items.length}</span></div>
            {items.map(r => (
              <div key={r.id} className="pipe-card" onClick={() => onSelect(r)}>
                <div className="pipe-card-name">{r.company}</div>
                <div className="pipe-card-co">{r.contact && r.contact !== '—' ? r.contact : TYPE_LABELS[r.type]}</div>
                {r.arr !== 'Investment TBD' && r.arr !== 'Board / Advisory' && <div className="pipe-card-arr">{r.arr}</div>}
                <Badge cls={priorityClass[r.priority]} label={r.priority} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('prospects');
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selected, setSelected] = useState(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentFilterIds, setAgentFilterIds] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [prospectNotes, setProspectNotes] = useState({});
  const [prospectStages, setProspectStages] = useState({});

  const tabData = useMemo(() => {
    switch (tab) {
      case 'partners': return PROSPECTS.filter(p => p.type === 'partner');
      case 'customers': return PROSPECTS.filter(p => p.type === 'federal' || p.type === 'customer');
      case 'investors': return PROSPECTS.filter(p => p.type === 'investor');
      default: return PROSPECTS;
    }
  }, [tab]);

  const filteredData = useMemo(() => {
    let base = agentFilterIds ? PROSPECTS.filter(p => agentFilterIds.includes(p.id)) : tabData;
    const q = search.toLowerCase();
    return base.filter(r => {
      const matchQ = !q || (r.company + r.contact + r.notes + (r.tags || []).join(' ') + r.oxDrivers + r.buyerDrivers).toLowerCase().includes(q);
      const matchP = !filterPriority || r.priority === filterPriority;
      const matchS = !filterStage || r.stage === filterStage;
      const matchT = !filterType || r.type === filterType;
      return matchQ && matchP && matchS && matchT;
    });
  }, [tabData, search, filterPriority, filterStage, filterType, agentFilterIds]);

  const handleAgentAction = useCallback((type, data) => {
    if (type === 'filter') {
      setAgentFilterIds(Array.isArray(data) ? data : null);
      setTab('prospects');
    } else if (type === 'highlight') {
      const rec = PROSPECTS.find(p => p.id === data);
      if (rec) { setSelected(rec); setHighlightId(data); setTab('prospects'); }
    } else if (type === 'update_stage') {
      setProspectStages(prev => ({ ...prev, [data.id]: data.newStage }));
    } else if (type === 'add_note') {
      setProspectNotes(prev => ({ ...prev, [data.id]: (prev[data.id] || '') + '\n[Agent note] ' + data.note }));
    }
  }, []);

  const clearAgentFilter = () => { setAgentFilterIds(null); setHighlightId(null); };

  if (!user) return <Login onLogin={setUser} />;

  const TABS = [
    { id:'prospects', label:'All Prospects', icon:'◎' },
    { id:'partners', label:'Channel Partners', icon:'⟁' },
    { id:'customers', label:'End Customers', icon:'⬜' },
    { id:'investors', label:'Investors', icon:'◈' },
    { id:'pipeline', label:'Pipeline', icon:'▦' },
  ];

  return (
    <div className="crm-shell">
      {/* TOP BAR */}
      <header className="topbar">
        <div className="topbar-left">
          <span className="topbar-logo">⬡ OrchestrateX</span>
          <span className="topbar-divider" />
          <span className="topbar-title">Quantum CRM · BD Pipeline</span>
        </div>
        <div className="topbar-right">
          <button
            className={`agent-toggle-btn ${agentOpen ? 'active' : ''}`}
            onClick={() => setAgentOpen(o => !o)}
            title="Open CRM Agent"
          >
            ⬡ CRM Agent {agentOpen ? '×' : '↗'}
          </button>
          <div className="user-chip">
            <span className="user-avatar">{user.avatar}</span>
            <span className="user-name">{user.name}</span>
            <span className="user-role">{user.role}</span>
          </div>
          <button className="logout-btn" onClick={() => setUser(null)}>Sign out</button>
        </div>
      </header>

      {/* NAV TABS */}
      <nav className="nav-tabs">
        {TABS.map(t => (
          <button key={t.id} className={`nav-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => { setTab(t.id); clearAgentFilter(); }}>
            <span className="tab-icon">{t.icon}</span>{t.label}
          </button>
        ))}
        {agentFilterIds && (
          <div className="agent-filter-badge">
            ⬡ Agent filter active — {agentFilterIds.length} records
            <button onClick={clearAgentFilter}>✕ Clear</button>
          </div>
        )}
      </nav>

      {/* BODY */}
      <div className="body-split">
        {/* MAIN CONTENT */}
        <main className="content" style={{ flex: 1, overflow: 'auto' }}>
          {tab === 'pipeline' ? (
            <Pipeline data={filteredData} onSelect={setSelected} />
          ) : (
            <>
              <StatsRow data={filteredData} />
              <div className="filter-row">
                <div className="search-wrap">
                  <span className="search-icon">⌕</span>
                  <input type="text" placeholder="Search prospects, contacts, drivers, tags…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                  <option value="">All priorities</option>
                  {['Critical','High','Medium','Low'].map(p => <option key={p}>{p}</option>)}
                </select>
                <select value={filterStage} onChange={e => setFilterStage(e.target.value)}>
                  <option value="">All stages</option>
                  {['Active','Pilot / Partnership Business Case','Pilot / Business Case / Negotiation','Negotiation','Discovery / Technical Validation','Discovery','Outreach','Pipeline'].map(s => <option key={s}>{s}</option>)}
                </select>
                {tab === 'prospects' && (
                  <select value={filterType} onChange={e => setFilterType(e.target.value)}>
                    <option value="">All types</option>
                    <option value="federal">Federal</option>
                    <option value="customer">Enterprise</option>
                    <option value="partner">Partner</option>
                    <option value="investor">Investor</option>
                  </select>
                )}
              </div>
              <ProspectsTable data={filteredData} onSelect={setSelected} highlightId={highlightId} />
            </>
          )}
        </main>

        {/* AGENT PANEL */}
        {agentOpen && (
          <aside className="agent-panel">
            <AgentPane user={user} onAgentAction={handleAgentAction} />
          </aside>
        )}
      </div>

      {/* DETAIL PANEL */}
      <DetailPanel record={selected} onClose={() => { setSelected(null); setHighlightId(null); }} />
    </div>
  );
}
